import clsx from 'clsx'
import first from 'lodash/first'
import map from 'lodash/map'
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import SvgBraintreeCard from '@Assets/SVG/BraintreeCard'
import SvgBraintreeCvv from '@Assets/SVG/BraintreeCvv'
import SvgBraintreeExpirationDate from '@Assets/SVG/BraintreeExpirationDate'
import SvgCancel from '@Assets/SVG/Cancel'
import SvgCheck from '@Assets/SVG/Check'
import LockIcon from '@Assets/SVG/Lock'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { Loading } from '@Components/Modal/Loading'
import { Button } from '@CoreComponents/Buttons/Button'
import { BraintreeContext } from '@Domains/Checkout/Providers/BraintreeContextProvider'
import { CardTypeIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import { PaymentProviderBusContext, PaymentProviders } from '@Domains/Checkout/Steps/Payment/PaymentProviderFactory/PaymentProviderBus'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  useBraintreeCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/Hooks/useBraintreeCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './BraintreeCreditCard.module.scss'

enum ButtonState {
  Submit = 'Submit',
  Idle = 'Idle',
}

const BraintreeStyles = {
  input: {
    'font-size': '16px',
    'font-family': '-apple-system, BlinkMacSystemFont,'
      + '"Segoe UI", "Roboto", "Oxygen",'
      + '"Ubuntu", "Cantarell", "Fira Sans",'
      + '"Droid Sans", "Helvetica Neue", sans-serif',
    'font-weight': '400',
    color: 'rgb(0, 17, 44)',
    'border-style': 'solid',
    'border-width': '2px',
  },
  ':placeholder-shown': {
    color: 'rgb(119,119,119)',
    'font-weight': 200,
  },
  ':focus': {
    outline: '0px solid rgb(119, 119, 119)',
    color: 'var(--black)',
  },

}

export const BraintreeCreditCardInfoComponent: FC<CheckoutPaymentComponentProps> = ({
  option,
  submitPayment,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const {
    busData,
    setBusData,
  } = useContext(PaymentProviderBusContext)(PaymentProviders.BraintreeCreditCard)
  const {
    failedNavigation,
    failedNavigationAttempts,
  } = useContext(CheckoutFlowContext)
  const triedSubmit = useMemo(
    () => failedNavigationAttempts && failedNavigationAttempts > 0 && failedNavigation === CheckoutFlowStep.SHIPPING,
    [failedNavigation, failedNavigationAttempts],
  )
  const { handleBraintreePaymentSubmitResponse } = useBraintreeCheckoutResponse()
  const {
    authorization,
    braintreeInstance,
    braintreeLoaded,
  } = useContext(BraintreeContext)
  const [cardNumberError, setCardNumberError] = useState(false)
  const [expirationDateError, setExpirationDateError] = useState(false)
  const [cvcError, setCvcError] = useState(false)
  const [expirationDateValid, setExpirationDateValid] = useState(false)
  const [cvcValid, setCvcValid] = useState(false)
  const [isAllValid, setIsAllValid] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [cardIcons, setCardIcons] = useState<string[]>([])
  const [cardImageError, setCardImageError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hostedFields, setHostedFields] = useState<any>(null)
  const [submitData, setSubmitData] = useState<any>(null)

  const mountBraintree = useCallback(async () => {
    if (!mounted && braintreeLoaded && braintreeInstance && braintreeInstance.hostedFields) {
      const hostedFieldsInstance = await (braintreeInstance.hostedFields as any).create({
        authorization,
        styles: BraintreeStyles,
        fields: {
          number: {
            selector: '#card-number',
            placeholder: '1234 5678 9012 3456',
          },
          cvv: {
            selector: '#cvv',
            placeholder: '123',
          },
          expirationDate: {
            selector: '#expiration-date',
            placeholder: 'MM/YY',
          },
        },
      })
      setMounted(!!hostedFieldsInstance)
      if (hostedFieldsInstance) {
        setCardIcons(hostedFieldsInstance.getState()
          .cards
          .map(card => card.type))
        setHostedFields(hostedFieldsInstance)
        setLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [braintreeInstance?.hostedFields, mounted, authorization])

  useEffect(() => {
    if (authorization && braintreeInstance?.hostedFields && !mounted && braintreeLoaded) {
      mountBraintree()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [braintreeInstance?.hostedFields, mounted, authorization, braintreeLoaded])

  useEffect(() => {
    if (busData === ButtonState.Submit) {
      setBusData(ButtonState.Idle)
      if (submitData?.formData && isAllValid) {
        submitPayment(submitData, response => handleBraintreePaymentSubmitResponse(option.type, response))
      }
      if (!isAllValid && hostedFields) {
        const state: any = hostedFields.getState()
        setCvcError(!state.fields.cvv.isValid)
        setExpirationDateError(!state.fields.expirationDate.isValid)
        setCardNumberError(!state.fields.number.isValid)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busData, hostedFields, isAllValid])

  const updateSubmitData = useCallback(() => {
    const state: any = hostedFields.getState()
    const { cvv } = state.fields
    const { expirationDate } = state.fields
    const cardNumber = state.fields.number
    const isValid = cvv.isValid && expirationDate.isValid && cardNumber.isValid
    setCvcValid(cvv.isValid)
    setExpirationDateValid(expirationDate.isValid)
    const cardBrand = state.cards.length === 1 ? (first(state.cards) as ({ type: string })).type : ''
    if (cardBrand !== submitData?.cardBrand) {
      setCardImageError(false)
    }
    setIsAllValid(state.fields.expirationDate.isValid && state.fields.cvv.isValid && state.fields.number.isValid)
    hostedFields.tokenize((err, payload) => {
      if (err) {
        return
      }
      setSubmitData(() => ({
        formData: payload,
        cardLastDigits: payload.details.lastFour,
        cardBrand,
        isValid,
        type: 'credit_card',
      }))
    })

    setSubmitData(prev => ({
      ...prev,
      cardBrand,
      isValid,
      type: 'credit_card',
    }))
  }, [hostedFields, submitData])

  useEffect(() => {
    if (hostedFields) {
      hostedFields.on('blur', ({
        emittedBy,
        fields,
      }) => {
        updateSubmitData()
        if (emittedBy === 'number') {
          setCardNumberError(fields.number.isEmpty && !triedSubmit ? false : !fields.number.isValid)
        }
        if (emittedBy === 'expirationDate') {
          setExpirationDateError(fields.expirationDate.isEmpty && !triedSubmit ? false : !fields.expirationDate.isValid)
        }
        if (emittedBy === 'cvv') {
          setCvcError(fields.cvv.isEmpty && !triedSubmit ? false : !fields.cvv.isValid)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostedFields])

  return (
    <div className={clsx(styles.braintreeCreditCardInfoComponent, cofDesignV1 && styles.cofDesignV1)}>
      <div className={styles.payInfo}>
        <p className={styles.payInfoHeader}>{t('checkout:COF_FILL_OUT_PAYMENT_INFO')}</p>
        <span className={clsx(styles.safeInfo)}>
          {!cofDesignV1 && (<SVGIcon icon={LockIcon} size="18px" />)}
          {t('checkout:PAYMENT_SAFE_TEXT')}
        </span>
      </div>
      <div className={styles.cardForm}>
        {loading && (
          <div className={styles.loading}>
            <Loading />
          </div>
        )}
        <form>

          <label className={clsx('hosted-fields--label', 'cvv-label', cardNumberError && styles.errorText)} htmlFor="card-number">
            {t('checkout:CARD_NUMBER')}
          </label>

          <div className={clsx('hosted-field', styles.cardNumber, cardNumberError && styles.errorBox)} id="card-number">
            {!cardNumberError && (
              <>
                {(cardImageError || !submitData?.cardBrand) && <SvgBraintreeCard />}
                {!cardImageError && submitData?.cardBrand && (
                  <CardTypeIcon
                    cardType={submitData.cardBrand}
                    onImageError={() => setCardImageError(true)}
                  />
                )}
              </>
            )}
            {cardNumberError && <SvgCancel className={styles.errorIcon} />}
          </div>
          {cardNumberError && <span className={styles.errorText}>{t('checkout:COF_CARD_NUMBER_IS_INVALID')}</span>}

          <div className={styles.twoColumns}>

            <div>
              <label className={clsx('hosted-fields--label', 'expirationDate-label', expirationDateError && styles.errorText)} htmlFor="expiration-date">
                {t('checkout:EXPIRY_DATE')}
              </label>
              <div className={clsx('hosted-field', styles.cardNumber, expirationDateError && styles.errorBox)} id="expiration-date">
                {!expirationDateValid && !expirationDateError && <SvgBraintreeExpirationDate />}
                {expirationDateError && <SvgCancel className={styles.errorIcon} />}
                {expirationDateValid && <SvgCheck className={styles.checkIcon} />}
              </div>
              {expirationDateError && <span className={styles.errorText}>{t('checkout:EXPIRY_DATE_IS_INVALID')}</span>}
            </div>

            <div>
              <label className={clsx('hosted-fields--label', 'cvv-label', cvcError && styles.errorText)} htmlFor="cvv">
                {t('checkout:CVC_LABEL')}
              </label>
              <div className={clsx('hosted-field', styles.cardNumber, cvcError && styles.errorBox)} id="cvv">
                {!cvcValid && !cvcError && <SvgBraintreeCvv />}
                {cvcError && <SvgCancel className={styles.errorIcon} />}
                {cvcValid && <SvgCheck className={styles.checkIcon} />}
              </div>
              {cvcError && <span className={styles.errorText}>{t('checkout:INVALID_CVC_CVV_FORMAT')}</span>}
            </div>

          </div>
        </form>
      </div>
      <div className={styles.cardIconsWrapper}>
        <span>{`${t('checkout:ACCEPTED_CARDS')}:`}</span>
        <div className={styles.cardIcons}>
          {map(cardIcons, icon => <CardTypeIcon key={icon} cardType={icon} />)}
        </div>
      </div>
    </div>
  )
}

export const BraintreeCreditCardButton: FC<CheckoutPaymentComponentProps> = () => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const { setBusData } = useContext(PaymentProviderBusContext)(PaymentProviders.BraintreeCreditCard)
  return (
    <div className={styles.payButtonWrapper}>
      <Button
        className={clsx(cofDesignV1 ? styles.payButtonCofDesignV1 : styles.payButton)}
        innerSpacing="medium"
        onClick={() => {
          setBusData(ButtonState.Submit)
        }}
        type="button"
      >
        {t('checkout:COF_GO_TO_SECURE_PAYMENT')}
      </Button>
    </div>
  )
}
